<template>
    <div class="iframe-widget">
        <div class="widget-header">
            <div class="left">
                <i
                    class="material-icons drag-handle"
                    v-tippy="{
                        placement: 'top',
                        theme: 'light',
                        arrow: true,
                        animation: 'scale-extreme',
                        duration: [200, 200],
                        hideOnClick: true,
                    }"
                    content="Sürükleyerek taşıyabilirsiniz"
                    >drag_indicator</i
                >
                <widget-title :widget="widget" :table="table" @widget-updated="handleWidgetUpdated" />
            </div>

            <widget-actions
                @fullscreen="handleFullscreen"
                @edit="handleEdit"
                @rename="handleRename"
                @duplicate="handleDuplicate"
                @dock="handleDock"
                @export="handleExport"
                @delete="$emit('delete', widget._id)"
                :title="widget.title"
                :widget="widget"
                :table="table"
                @widget-updated="handleWidgetUpdated"
                @widget-duplicated="handleWidgetDuplicated"
                @widget-deleted="handleWidgetDeleted"
            />
        </div>
        <div class="widget-content">
            <empty-widget
                v-if="!hasValidSettings"
                :error="null"
                :has-valid-settings="hasValidSettings"
                @edit="handleEdit"
            />
            <iframe
                v-else
                :src="widget.settings.url"
                :height="widget.settings.height"
                frameborder="0"
                class="iframe-content"
                :key="refreshKey"
            ></iframe>
        </div>

        <iframe-settings-modal
            v-if="showSettingsModal"
            :visible="showSettingsModal"
            :widget="widget"
            @close="showSettingsModal = false"
            @save="handleSettingsSave"
            :table="table"
        />
    </div>
</template>

<script>
import Vue from "vue";
import { TABLE_API } from "../../../../../../api/table";
import log2 from "../../../../../../utils/log2";
import WidgetActions from "../charts/components/WidgetActions.vue";
import WidgetTitle from "../charts/components/WidgetTitle.vue";
import EmptyWidget from "../EmptyWidget.vue";
import IframeSettingsModal from "./IframeSettingsModal.vue";

const log = log2("IframeWidget");

export default Vue.extend({
    name: "IframeWidget",
    components: {
        WidgetActions,
        WidgetTitle,
        EmptyWidget,
        IframeSettingsModal,
    },
    props: {
        widget: {
            type: Object,
            required: true,
        },
        table: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            showSettingsModal: false,
            refreshKey: 0,
            refreshInterval: null,
        };
    },
    computed: {
        hasValidSettings() {
            return !!this.widget.settings && !!this.widget.settings.url;
        },
    },
    mounted() {
        this.setupRefreshInterval();
    },
    beforeDestroy() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
        }
    },
    methods: {
        setupRefreshInterval() {
            if (this.refreshInterval) {
                clearInterval(this.refreshInterval);
            }

            const interval = this.widget.settings?.refreshInterval;
            if (interval && interval > 0) {
                this.refreshInterval = setInterval(() => {
                    this.refreshKey++;
                }, interval * 1000);
            }
        },
        handleFullscreen() {
            // TODO: Tam ekran modunu implement et
        },
        async handleRename(newName) {
            try {
                await TABLE_API.updateWidget(this.table._id, this.widget.page, this.widget._id, {
                    title: newName,
                });
                this.$emit("widget-updated", {
                    ...this.widget,
                    title: newName,
                });
            } catch (error) {
                console.error("Widget adı güncellenirken hata:", error);
                throw error;
            }
        },
        async handleDuplicate() {
            try {
                const result = await TABLE_API.duplicateWidget(this.table._id, this.widget.page, this.widget._id);
                this.$message.success("Widget başarıyla çoğaltıldı");
                this.$emit("widget-duplicated", {
                    widget: result.widget,
                    page: result.page,
                });
            } catch (error) {
                console.error("Widget çoğaltılırken hata:", error);
                this.$message.error("Widget çoğaltılırken bir hata oluştu");
            }
        },
        handleDock() {
            // TODO: Sabitleme işlemini implement et
        },
        handleExport() {
            // TODO: Dışa aktarma işlemini implement et
        },
        handleEdit() {
            this.showSettingsModal = true;
        },
        async handleSettingsSave(newSettings) {
            try {
                await TABLE_API.updateWidget(this.table._id, this.widget.page, this.widget._id, {
                    settings: newSettings,
                });
                this.$message.success("Widget ayarları güncellendi");
                this.$emit("widget-updated", {
                    ...this.widget,
                    settings: newSettings,
                });
                this.showSettingsModal = false;
                this.setupRefreshInterval();
            } catch (error) {
                console.error("Widget ayarları güncellenirken hata:", error);
                this.$message.error("Widget ayarları güncellenirken bir hata oluştu");
            }
        },
        async handleWidgetUpdated(updatedWidget) {
            this.$emit("widget-updated", updatedWidget);
        },
        async handleWidgetDuplicated(result) {
            this.$emit("widget-duplicated", result);
        },
        async handleWidgetDeleted(widgetId) {
            this.$emit("delete", widgetId);
        },
    },
    watch: {
        "widget.settings": {
            handler() {
                this.setupRefreshInterval();
            },
            deep: true,
        },
    },
});
</script>

<style scoped lang="scss">
.iframe-widget {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 4px;
    overflow: hidden;
    position: relative;
    border: 2px solid #fafafa;
    transition: border-color 0.2s ease;

    &:hover {
        border-color: #1890ff;

        .drag-handle {
            visibility: visible;
            line-height: 1px;
        }
    }

    .drag-handle {
        transform: translateY(-50%);
        font-size: 20px;
        color: rgba(0, 0, 0, 0.25);
        cursor: move;
        transition: opacity 0.2s, color 0.2s, left 0.2s;
        visibility: hidden;
        line-height: 1px;
        &:hover {
            color: rgba(0, 0, 0, 0.45);
            left: -8px;
        }
    }

    .widget-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 16px;
        border-bottom: 1px solid #e8e8e8;
        background: #fafafa;
        .left {
            display: flex;
            align-items: center;
            gap: 5px;
            width: -webkit-fill-available;
        }
    }

    .widget-content {
        flex: 1;
        min-height: 0;
        position: relative;

        .iframe-content {
            width: 100%;
            border: none;
            height: 100%;
        }
    }
}
</style> 